import { useTranslation } from 'react-i18next';
import React, { useEffect,useState } from 'react';
import { Button, Radio, DatePicker, Select, message, Space, Divider, Tabs, Tag } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { LIST_HUB_SORT_CODE_SELECT_OPTIONS } from '../graphql/queries/listAllHubSortCodeOptions';
import { LIST_WAREHOUSES_UNDER_HUB_OPTIONS } from '../graphql/queries/listWarehousesUnderHubOptions';
import {
  BIND_PRICING_SCHEME,
} from '../graphql/mutations/pricingSchemeApplyMutation';
import { GET_PRICING_SCHEME_DETAIL, PricingSchemeDetailResponse } from 'graphql/queries/settlement/getPricingSchemeDetail';
import { ResourceCode } from 'constants/resourceCodes';
import { HasResource } from 'components/HasResource';
import { BUSINESS_SCENARIO_COST_PERIODIC, COST_PERIODIC_FEE_ITEM_CODE, COST_PERIODIC_BIND_ALL_VALUE } from 'graphql/queries/settlement/getSettlementCommonQueries';
import { LIST_SCHEME_SELECT_OPTIONS } from '../../graphql/queries/listSchemeSelectOptions';
import { formatTierDiscountDisplay } from '../../periodic/CostPeriodicTierConfig';
import { resolvePricingSchemeMessage } from 'utils/graphqlUtils';

interface Props {
  onSuccess?: () => void;
  onCancel?: () => void;
}


// 已选绑定维度的一行：L1=全部HUB、L2=HUB+全部Warehouse、L3=具体Warehouse
interface SelectedBinding {
  level: 'L1' | 'L2' | 'L3';
  hub: string;
  bindValue: string;
  label: string;
}

const CostPeriodicBindSchemeComp: React.FC<Props> = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [selectedSchemeId, setSelectedSchemeId] = useState<number | null>(null);
  // 维度层级单选：false=指定HUB/Warehouse（默认），true=全部HUB
  const [useAllHubs, setUseAllHubs] = useState(false);
  const [currentHub, setCurrentHub] = useState<string | null>(null);
  // Warehouse 互斥：'ALL'=全部（含未来新增）=L2（默认），'SPECIFIC'=指定Warehouse=L3
  const [warehouseMode, setWarehouseMode] = useState<'ALL' | 'SPECIFIC'>('ALL');
  const [selectedWarehouses, setSelectedWarehouses] = useState<string[]>([]);
  const [selectedBindings, setSelectedBindings] = useState<SelectedBinding[]>([]);
  const [effectiveNow, setEffectiveNow] = useState(true);
  const [effectiveDate, setEffectiveDate] = useState<string | null>(null);
  const [timezone, setTimezone] = useState('Asia/Shanghai');

  const { data: schemeOptions } = useQuery(LIST_SCHEME_SELECT_OPTIONS, {
    variables: { businessScenario: BUSINESS_SCENARIO_COST_PERIODIC, channelId: 0, isBind: false },
  });
  const { data: hubOptions } = useQuery(LIST_HUB_SORT_CODE_SELECT_OPTIONS);
  const { data: warehouseOptions } = useQuery(LIST_WAREHOUSES_UNDER_HUB_OPTIONS, {
    variables: { hubSortingCode: currentHub },
    skip: !currentHub,
  });
  const { data: detailData, loading: detailLoading } = useQuery<PricingSchemeDetailResponse>(GET_PRICING_SCHEME_DETAIL, {
    variables: { pricingSchemeId: selectedSchemeId },
    skip: !selectedSchemeId,
    fetchPolicy: 'network-only',
  });

  const [bindScheme, { loading }] = useMutation(BIND_PRICING_SCHEME);

  useEffect(() => {
    const detailResult = detailData?.getPricingSchemeDetail;
    if (detailResult?.messageKey || detailResult?.message) {
      message.error(resolvePricingSchemeMessage(detailResult, '加载定价方案详情失败'));
    }
  }, [detailData]);

  const getTiers = () => {
    const priceConfig = detailData?.getPricingSchemeDetail?.priceConfig;
    if (!priceConfig?.length) return [];
    const config = priceConfig.find(p => Number(p.pricingFeeItemCode) === COST_PERIODIC_FEE_ITEM_CODE);
    const raw = config?.allPriceConfig?.[0]?.contentPriceConfig;
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  };

  // 将当前 HUB+Warehouse 选择追加到右侧「已选绑定维度」，并重置选择区便于连续添加
  const handleAddToSelected = () => {
    if (!currentHub) { message.error('请选择HUB'); return; }
    const existing = new Set(selectedBindings.map(b => b.bindValue));
    const toAdd: SelectedBinding[] =
      warehouseMode === 'ALL'
        ? [{ level: 'L2', hub: currentHub, bindValue: currentHub, label: `${currentHub} / 全部 Warehouse` }]
        : selectedWarehouses.map(wh => {
            const opt = warehouseOptions?.allOptions?.find((o: { value: string; label: string }) => o.value === wh);
            return { level: 'L3' as const, hub: currentHub, bindValue: wh, label: opt?.label ?? wh };
          });
    if (warehouseMode === 'SPECIFIC' && toAdd.length === 0) { message.error('请至少选择一个Warehouse'); return; }
    const deduped = toAdd.filter(b => !existing.has(b.bindValue));
    if (deduped.length < toAdd.length) { message.warning('该维度已在已选列表中'); }
    if (deduped.length === 0) return;
    setSelectedBindings([...selectedBindings, ...deduped]);
    // 仅清空 warehouse 多选框，保留维度层级与 HUB 便于连续添加同粒度维度
    setSelectedWarehouses([]);
  };

  const handleRemoveSelected = (bindValue: string) => {
    setSelectedBindings(selectedBindings.filter(b => b.bindValue !== bindValue));
  };

  const canConfirm = !!selectedSchemeId && (useAllHubs || selectedBindings.length > 0);

  const handleBind = async () => {
    if (!selectedSchemeId) { message.error('请选择方案'); return; }
    const bindValue = useAllHubs ? [COST_PERIODIC_BIND_ALL_VALUE] : selectedBindings.map(b => b.bindValue);
    if (bindValue.length === 0) { message.error('请选择绑定维度'); return; }
    if (!effectiveNow && !effectiveDate) { message.error('请选择生效日期'); return; }

    try {
      const { data, errors } = await bindScheme({
        variables: {
          input: {
            clientId: 0,
            pricingSchemeId: selectedSchemeId,
            bindValue,
            effectiveNow,
            effectiveDate: effectiveNow ? null : effectiveDate,
            timezone,
          },
        },
      });
      if (errors?.length) { message.error(errors[0].message); return; }
      if (data?.bindPricingSchema?.success) {
        message.success(t('settlement.priceCenter.messages.bindSuccess'));
        onSuccess?.();
      } else {
        message.error(resolvePricingSchemeMessage(data?.bindPricingSchema, t('settlement.priceCenter.messages.bindFailed')));
      }
    } catch (e: any) {
      message.error(e?.message || t('errors.INTERNAL_ERROR'));
    }
  };

  const tiers = getTiers();

  const schemeDetail = detailData?.getPricingSchemeDetail;

  // 阶梯纯文本展示（不使用 InputNumber）
  const feeTabContent = tiers.length > 0 ? (
    <div>
      {/* 列头 */}
      <div style={{ display: 'flex', fontWeight: 500, fontSize: 13, color: '#555', paddingBottom: 8, borderBottom: '1px solid #f0f0f0', marginBottom: 8 }}>
        <span style={{ width: 160, flexShrink: 0 }}>费用类型</span>
        <span style={{ flex: 1 }}>阶梯</span>
        <span style={{ width: 170, flexShrink: 0, textAlign: 'right' }}>折扣类型</span>
      </div>
      {/* 数据行 */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <span style={{ width: 160, flexShrink: 0, paddingTop: 4 }}>DSP基础运费</span>
        <div style={{ flex: 1 }}>
          {tiers.map((t: any, i: number) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ flex: 1, color: '#333' }}>{t.startOrderCount} - {t.endOrderCount} 单</span>
              <span style={{ width: 170, textAlign: 'right', color: '#888' }}>{formatTierDiscountDisplay(t)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div>
      {/* 方案选择 + 备注 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
        <span className="font-medium">选择方案</span>
        <Select
          style={{ width: 200 }}
          placeholder="请选择"
          options={schemeOptions?.allOptions?.map((o: { value: string; label: string }) => ({
            value: Number(o.value),
            label: o.label,
          }))}
          value={selectedSchemeId}
          onChange={v => { setSelectedSchemeId(v); }}
        />
        <span className="font-medium ml-4">方案备注</span>
        <span style={{ color: '#555' }}>{schemeDetail?.pricingSchemeDescription ?? ''}</span>
      </div>

      {/* 方案详情预览 */}
      {detailLoading && selectedSchemeId && (
        <div style={{ padding: '12px 0', color: '#999' }}>加载方案配置中...</div>
      )}
      {!detailLoading && feeTabContent && (
        <Tabs
          size="middle"
          items={[{ key: 'nthOrderSameAddrDiscount', label: '第N件N折', children: feeTabContent }]}
          style={{ marginBottom: 0 }}
        />
      )}

      <Divider />

      {/* 绑定维度 */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 500, marginBottom: 8 }}>绑定纬度：</div>
        <div style={{ display: 'flex', gap: 24 }}>
          {/* 左侧：选择区 */}
          <div style={{ flex: 1 }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Radio
                checked={!useAllHubs}
                onChange={() => setUseAllHubs(false)}
              >
                指定 HUB / Warehouse
              </Radio>
              {!useAllHubs && (
                <div style={{ paddingLeft: 24 }}>
                  <Space direction="vertical" size={10} style={{ width: '100%' }}>
                    <Space align="center" size={8}>
                      <span style={{ width: 72 }}>HUB</span>
                      <Select
                        showSearch
                        style={{ width: 220 }}
                        placeholder="请选择"
                        optionFilterProp="label"
                        options={hubOptions?.allOptions}
                        value={currentHub}
                        onChange={v => { setCurrentHub(v); setWarehouseMode('ALL'); setSelectedWarehouses([]); }}
                      />
                    </Space>
                    <Space align="start" size={8}>
                      <span style={{ width: 72, lineHeight: '32px' }}>Warehouse</span>
                      <Space direction="vertical" size={8}>
                        <Radio
                          checked={warehouseMode === 'ALL'}
                          disabled={!currentHub}
                          onChange={() => { setWarehouseMode('ALL'); setSelectedWarehouses([]); }}
                        >
                          全部（含未来新增）
                        </Radio>
                        <Space align="center" size={8}>
                          <Radio
                            checked={warehouseMode === 'SPECIFIC'}
                            disabled={!currentHub}
                            onChange={() => setWarehouseMode('SPECIFIC')}
                          >
                            指定 Warehouse
                          </Radio>
                          {warehouseMode === 'SPECIFIC' && (
                            <Select
                              mode="multiple"
                              showSearch
                              style={{ width: 260 }}
                              placeholder="按二段码搜索"
                              optionFilterProp="label"
                              options={warehouseOptions?.allOptions}
                              value={selectedWarehouses}
                              onChange={setSelectedWarehouses}
                            />
                          )}
                        </Space>
                      </Space>
                    </Space>
                    <Button
                      size="small"
                      onClick={handleAddToSelected}
                      disabled={!currentHub || (warehouseMode === 'SPECIFIC' && selectedWarehouses.length === 0)}
                    >
                      加入已选
                    </Button>
                  </Space>
                </div>
              )}
              <Radio
                checked={useAllHubs}
                onChange={() => { setUseAllHubs(true); setCurrentHub(null); setWarehouseMode('ALL'); setSelectedWarehouses([]); setSelectedBindings([]); }}
              >
                全部 HUB
              </Radio>
            </Space>
          </div>

          {/* 右侧：已选绑定维度 */}
          <div style={{ width: 320, borderLeft: '1px solid #f0f0f0', paddingLeft: 16 }}>
            <div style={{ fontWeight: 500, marginBottom: 8 }}>已选绑定维度</div>
            {useAllHubs ? (
              <Tag>全部 HUB</Tag>
            ) : selectedBindings.length === 0 ? (
              <span style={{ color: '#999' }}>暂无</span>
            ) : (
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                {selectedBindings.map(b => (
                  <Tag key={b.bindValue} closable onClose={() => handleRemoveSelected(b.bindValue)}>
                    {b.label}
                  </Tag>
                ))}
              </Space>
            )}
          </div>
        </div>
      </div>

      <Divider />

      {/* 生效时间 */}
      <div style={{ marginBottom: 16 }}>
        <Space align="start" size={12}>
          <span style={{ fontWeight: 500, lineHeight: '32px' }}>生效时间：</span>
          <Space direction="vertical" size={8}>
            <Radio checked={effectiveNow} onChange={() => setEffectiveNow(true)}>立即生效</Radio>
            <Space align="center" size={8}>
              <Radio checked={!effectiveNow} onChange={() => setEffectiveNow(false)}>未来生效</Radio>
              {!effectiveNow && (
                <DatePicker
                  disabledDate={d => d && d < dayjs().startOf('day')}
                  onChange={d => setEffectiveDate(d ? d.format('YYYY-MM-DD') : null)}
                />
              )}
              {!effectiveNow && <span>时区</span>}
              {!effectiveNow && (
                <Select
                  style={{ width: 200 }}
                  placeholder="请选择"
                  value={timezone}
                  onChange={setTimezone}
                  options={[
                    { value: 'Asia/Shanghai', label: 'Asia/Shanghai' },
                    { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
                  ]}
                />
              )}
            </Space>
          </Space>
        </Space>
      </div>

      <div className="flex justify-end gap-4">
        <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PERIODIC_PRICE_APPLY_OPERATE]}>
          <Button type="primary" onClick={handleBind} loading={loading} disabled={!canConfirm}>确认绑定</Button>
        </HasResource>
        <Button onClick={onCancel}>返回</Button>
      </div>
    </div>
  );
};

export default CostPeriodicBindSchemeComp;
